import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from 'react'
import { authService } from '@/services/auth.service'
import { tokenStorage } from '@/lib/api'
import type { User } from '@/types/auth.types'

export const LAST_ACTIVITY_KEY = 'seven_analytics_last_activity'
export const INACTIVITY_TIMEOUT_MS = 14400000 // 4 horas padrão de inatividade

interface AuthContextValue {
  user: User | null
  loading: boolean
  mfaPending: boolean
  login: (email: string, password: string) => Promise<void>
  loginPersonal: (email: string, password: string) => Promise<any>
  registerPersonal: (payload: { full_name: string; email: string; password: string; cpf?: string }) => Promise<any>
  verifyMfa: (code: string, trustDevice: boolean) => Promise<void>
  logout: () => Promise<void>
  cancelMfa: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [mfaPending, setMfaPending] = useState(false)
  const [mfaToken, setMfaToken] = useState<string | null>(null)

  const lastActivityRef = useRef<number>(Date.now())

  useEffect(() => {
    const token = tokenStorage.get()
    if (!token) {
      setLoading(false)
      return
    }

    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY)
    const now = Date.now()
    if (lastActivity && now - Number(lastActivity) > INACTIVITY_TIMEOUT_MS) {
      tokenStorage.remove()
      tokenStorage.removeRefreshToken()
      setLoading(false)
      window.location.replace('/login?expired=true')
      return
    }

    authService
      .getMe()
      .then(setUser)
      .catch((err: any) => {
        // Limpa a sessão SOMENTE se for um erro 401 ou token/sessão expirada
        // Erros de rate limit (429) ou oscilações de rede mantêm os tokens preservados.
        if (
          err?.status === 401 ||
          err?.message?.includes('Token inválido') ||
          err?.message?.includes('Unauthorized') ||
          err?.message?.includes('Sessão expirada') ||
          err?.message?.includes('expirad')
        ) {
          tokenStorage.remove()
          tokenStorage.removeRefreshToken()
        } else {
          console.warn('[AuthContext] Falha temporária em getMe sem 401 (preservando sessão):', err)
        }
      })
      .finally(() => setLoading(false))
  }, [])

  // Activity Tracking
  useEffect(() => {
    if (!user) return

    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()))
    lastActivityRef.current = Date.now()

    const updateActivity = () => {
      const now = Date.now()
      if (now - lastActivityRef.current > 10000) {
        lastActivityRef.current = now
        localStorage.setItem(LAST_ACTIVITY_KEY, String(now))
      }
    }

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove']
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true })
    })

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity)
      })
    }
  }, [user])

  // Periodic Inactivity Checker
  useEffect(() => {
    if (!user) return

    const interval = setInterval(() => {
      const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY)
      const now = Date.now()
      if (lastActivity && now - Number(lastActivity) > INACTIVITY_TIMEOUT_MS) {
        tokenStorage.remove()
        tokenStorage.removeRefreshToken()
        setUser(null)
        setMfaPending(false)
        setMfaToken(null)
        window.location.replace('/login?expired=true')
      }
    }, 30000)

    return () => clearInterval(interval)
  }, [user])

  async function login(email: string, password: string) {
    const response = await authService.login({ email, password })
    if ('requiresMfa' in response) {
      setMfaToken(response.mfaToken)
      setMfaPending(true)
    } else {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()))
      setUser(response.user)
    }
  }

  async function loginPersonal(email: string, password: string) {
    const response = await authService.loginPersonal({ email, password })
    if ('requiresMfa' in response) {
      setMfaToken(response.mfaToken)
      setMfaPending(true)
      return response
    } else {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()))
      setUser(response.user)
      return response
    }
  }

  async function registerPersonal(payload: { full_name: string; email: string; password: string; cpf?: string }) {
    const response = await authService.registerPersonal(payload)
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()))
    setUser(response.user)
    return response
  }

  async function verifyMfa(code: string, trustDevice: boolean) {
    if (!mfaToken) throw new Error('MFA token missing')
    const response = await authService.verifyMfa({ mfaToken, code, trustDevice })
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()))
    setUser(response.user)
    setMfaPending(false)
    setMfaToken(null)
  }

  async function logout() {
    await authService.logout()
    setUser(null)
    setMfaPending(false)
    setMfaToken(null)
  }

  function cancelMfa() {
    setMfaPending(false)
    setMfaToken(null)
  }

  return (
    <AuthContext.Provider value={{ user, loading, mfaPending, login, loginPersonal, registerPersonal, verifyMfa, logout, cancelMfa }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuthContext() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuthContext must be used inside AuthProvider')
  return ctx
}
